import React, { useEffect, useState } from "react";
import api from "../../../api";
import Tooltip from "../../../components/ui/Tooltip";
import { UserPlus, Trash2, Shield, Users, Mail, X, Key, Search, FileText, UserX, UserCheck, Edit2, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "../../../context/NotificationContext";
import { useAuth } from "../../../context/AuthProvider";
import RolesManager from "../../../pages/settings/RolesManager";
import AlertModal from "../../../components/ui/AlertModal";
import CustomSelect from "../../../components/ui/CustomSelect";
import MiniLoader from "../../../components/MiniLoader";

export default function Equipe() {
  const { showToast } = useNotification();
  const { user: currentUser } = useAuth();
  
  // Update this to use the new jobTitle or roles instead of Rôle
  const currentUserRole = currentUser?.jobTitle || "Utilisateur";
  const hasAdminRights = currentUser?.permissions?.some(p => p.code === "MANAGE_USERS") || currentUserRole === "Administrateur Système" || currentUserRole === "Administrateur";

  const [activeTab, setActiveTab] = useState("membres");

  const [utilisateurs, setUtilisateurs] = useState([]);
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Conseiller");
  const [sendEmail, setSendEmail] = useState(true);
  const [openUserModal, setOpenUserModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [roleToAssign, setRoleToAssign] = useState("");

  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [rolesWorkspace, setRolesWorkspace] = useState([]);

  useEffect(() => {
    if (activeTab === "membres") fetchUtilisateurs();
    if (activeTab === "audit") fetchAuditData();
    fetchRoles();
  }, [activeTab]);

  const fetchRoles = async () => {
    try {
      const res = await api.get("/roles");
      if (Array.isArray(res.data)) {
        setRolesWorkspace(res.data);
        if (res.data.length > 0 && role === "Conseiller") {
          setRole(res.data[0].name);
        }
      }
    } catch (err) {
      console.error("Erreur roles:", err);
    }
  };

  const fetchUtilisateurs = async () => {
    try {
      const res = await api.get("/utilisateurs");
      if (Array.isArray(res.data)) setUtilisateurs(res.data);
    } catch (err) {
      console.error("Erreur lors de la recup des utilisateurs :", err);
    }
  };

  const fetchAuditData = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get("/audit-access/dossiers");
      setAuditData(res.data);
    } catch (err) {
      console.error("Erreur audit:", err);
      showToast("Impossible de charger l'audit", "error");
    } finally {
      setAuditLoading(false);
    }
  };

  const sendInvitationEmail = async (userEmail, userPassword, userRole) => {
    try {
      const templateParams = { to_email: userEmail, password: userPassword, role: userRole, link: window.location.origin };
      console.log("EmailJS Sending:", templateParams);
    } catch (err) {
      console.error("Erreur EmailJS:", err);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await api.put(`/utilisateurs/${userId}/toggle-status`);
      fetchUtilisateurs();
      showToast("Statut mis a jour", "success");
    } catch (err) {
      showToast("Erreur lors de la mise a jour du statut", "error");
    }
  };

  const handleResendInvitation = async (user) => {
    try {
      const response = await api.post(`/utilisateurs/${user.id}/reset-password`);
      const { newPassword } = response.data;
      await sendInvitationEmail(user.Email, newPassword, user.jobTitle || "Conseiller");
      showToast("Invitation renvoyee avec succes", "success");
    } catch (err) {
      showToast("Erreur lors du renvoi de l'invitation", "error");
    }
  };

  const ajouterUtilisateur = async (e) => {
    e.preventDefault();
    try {
      if (!nom || !email || !password) return;
      const selectedRoleObj = rolesWorkspace.find(r => r.name === role);
      if (!selectedRoleObj) {
         showToast("Veuillez sélectionner un rôle valide", "error");
         return;
      }
      await api.post("/utilisateurs/register", { Nom: nom, Email: email, Password: password, roleId: selectedRoleObj.id, jobTitle: role });
      if (sendEmail) await sendInvitationEmail(email, password, role);
      fetchUtilisateurs();
      setNom(""); setEmail(""); setPassword(""); setRole("Conseiller");
      setOpenUserModal(false);
      showToast("Utilisateur ajoute avec succes", "success");
    } catch (err) {
      showToast("Erreur lors de l'ajout de l'utilisateur.", "error");
    }
  };

  const handleUpdateRole = async (e) => {
    if(e) e.preventDefault();
    try {
      const selectedRoleObj = rolesWorkspace.find(r => r.name === roleToAssign);
      if (!selectedRoleObj) {
         showToast("Veuillez sélectionner un rôle valide", "error");
         return;
      }
      await api.put(`/utilisateurs/${selectedUserId}/role`, { roleId: selectedRoleObj.id });
      setEditRoleModal(false);
      setSelectedUserId(null);
      fetchUtilisateurs();
      showToast("Rôle mis à jour avec succès", "success");
    } catch (err) {
      showToast("Erreur lors de la mise à jour du rôle", "error");
    }
  };

  const supprimerUtilisateur = async () => {
    try {
      await api.delete(`/utilisateurs/${selectedUserId}`);
      await fetchUtilisateurs();
      showToast("Utilisateur supprime", "success");
    } catch (err) {
      console.error("Delete error:", err);
      showToast(err.response?.data?.message || "Erreur lors de la suppression.", "error");
    } finally {
      setConfirmDelete(false);
      setSelectedUserId(null);
    }
  };

  const RoleBadge = ({ role }) => {
    if (!role) {
      return <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">@everyone</span>;
    }
    if (["Administrateur", "Admin", "Administrator", "Administrateur Système"].includes(role)) {
      return <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-semibold bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)]"><Shield className="w-3 h-3" /> {role}</span>;
    }
    if (role === "Associé") {
      return <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-semibold bg-[var(--hp-info-50)] text-[var(--hp-info-700)]"><Users className="w-3 h-3" /> {role}</span>;
    }
    return <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-semibold bg-[var(--hp-success-50)] text-[var(--hp-success-700)]">{role}</span>;
  };

  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="px-6 pt-6 shrink-0 border-b border-[var(--hp-color-border-muted)]">
        <div className="flex justify-between items-center mb-6 pr-12">
          <div>
            <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              Équipe, Rôles & Accès
            </h2>
            <p className="text-sm text-tertiary mt-1">Tour de contrôle des collaborateurs et de leurs permissions.</p>
          </div>
          {activeTab === "membres" && hasAdminRights && (
            <button onClick={() => setOpenUserModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium rounded-xl shadow-sm hover:bg-primary-hover transition">
              <UserPlus className="w-4 h-4" />
              Inviter un membre
            </button>
          )}
        </div>
        <div className="flex gap-6 border-b border-transparent">
          <button
            onClick={() => setActiveTab("membres")}
            className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'membres' ? 'text-primary' : 'text-subtle hover:text-primary'}`}
          >
            Membres
            {activeTab === 'membres' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full"></span>}
          </button>
          {hasAdminRights && (
            <button
              onClick={() => setActiveTab("roles")}
              className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'roles' ? 'text-primary' : 'text-subtle hover:text-primary'}`}
            >
              Matrice des Rôles
              {activeTab === 'roles' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full"></span>}
            </button>
          )}
          <button
            onClick={() => setActiveTab("audit")}
            className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'audit' ? 'text-primary' : 'text-subtle hover:text-primary'}`}
          >
            Audit des Accès
            {activeTab === 'audit' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-full"></span>}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-workspace relative">
        <AnimatePresence mode="wait">
          {activeTab === "membres" && (
            <motion.div key="membres" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="p-6 h-full">
              <div className="bg-surface border border-[var(--hp-color-border-muted)] rounded-xl shadow-sm overflow-hidden flex flex-col h-full">
                <div className="overflow-x-hidden overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-200">
                  <table className="w-full text-left text-sm text-[var(--hp-color-text)]">
                    <thead className="bg-surface-muted sticky top-0 z-10 border-b border-[var(--hp-color-border-muted)]">
                      <tr>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Utilisateur</th>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Email</th>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Rôle</th>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Statut</th>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">2FA</th>
                        <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Connexion / Création</th>
                        {hasAdminRights && (
                          <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs text-right w-[1%] whitespace-nowrap">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--hp-color-border-subtle)]">
                      {utilisateurs.map((user) => (
                        <tr key={user.id} className="hover:bg-surface-muted transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[var(--hp-info-100)] text-[var(--hp-info-700)] flex items-center justify-center font-bold text-sm shrink-0">
                                {user.Nom ? user.Nom[0].toUpperCase() : "?"}
                              </div>
                              <span className="font-medium text-primary">{user.Nom}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-subtle flex items-center gap-2 break-all">
                            <Mail className="w-4 h-4 shrink-0" /> {user.Email}
                          </td>
                          <td className="px-6 py-4">
                            <RoleBadge role={user.jobTitle || user.Rôle} />
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${user.status === 'ACTIVE' ? 'bg-[var(--hp-success-50)] text-[var(--hp-success-700)]' : user.status === 'LOCKED' ? 'bg-[var(--hp-warning-50)] text-[var(--hp-warning-700)]' : 'bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)]'}`}>{user.status === 'ACTIVE' ? 'Actif' : user.status === 'LOCKED' ? 'Bloqué' : 'Inactif'}</span>
                          </td>
                          <td className="px-6 py-4">
                            {user.twoFactorEnabled ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--hp-success-700)]"><Shield className="w-3 h-3" /> Activé</span> : <span className="text-xs text-subtle">Désactivé</span>}
                          </td>
                          <td className="px-6 py-4 text-xs text-subtle whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <span><span className="font-medium">Dernière co :</span> {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('fr-FR') : 'Jamais'}</span>
                              <span><span className="font-medium">Créé le :</span> {user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '-'}</span>
                            </div>
                          </td>
                          {hasAdminRights && (
                            <td className="px-6 py-4 text-right w-[1%] whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                {user.jobTitle !== "Administrateur Système" && (
                                  <Tooltip content={user.status === 'ACTIVE' ? "Suspendre l'accès" : "Réactiver l'accès"}>
                                    <button onClick={() => handleToggleStatus(user.id)} className={`transition p-2 rounded-lg ${user.status === 'ACTIVE' ? 'text-subtle hover:text-[var(--hp-warning-600)] hover:bg-[var(--hp-warning-50)]' : 'text-subtle hover:text-[var(--hp-success-600)] hover:bg-[var(--hp-success-50)]'}`}>
                                      {user.status === 'ACTIVE' ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
                                    </button>
                                  </Tooltip>
                                )}
                                {user.jobTitle !== "Administrateur Système" && (
                                  <Tooltip content="Modifier le rôle">
                                    <button onClick={() => { setSelectedUserId(user.id); setRoleToAssign(user.jobTitle || ""); setEditRoleModal(true); }} className="text-subtle hover:text-[var(--hp-info-600)] transition p-2 rounded-lg hover:bg-[var(--hp-info-50)]">
                                      <Edit2 className="w-5 h-5" />
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip content="Renvoyer l'invitation">
                                  <button onClick={() => handleResendInvitation(user)} className="text-subtle hover:text-primary transition p-2 rounded-lg hover:bg-workspace">
                                    <Mail className="w-5 h-5" />
                                  </button>
                                </Tooltip>
                                {!(user.lastLoginAt && new Date(user.lastLoginAt).getFullYear() > 1970) && user.jobTitle !== "Administrateur Système" && (
                                  <Tooltip content="Supprimer l'utilisateur">
                                    <button onClick={() => { setSelectedUserId(user.id); setConfirmDelete(true); }} className="text-subtle hover:text-[var(--hp-danger-600)] transition p-2 rounded-lg hover:bg-[var(--hp-danger-50)]">
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {utilisateurs.length === 0 && (
                        <tr><td colSpan={hasAdminRights ? "7" : "6"} className="px-6 py-12 text-center text-subtle">Aucun utilisateur.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "roles" && hasAdminRights && (
            <motion.div key="roles" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="h-full">
              <RolesManager />
            </motion.div>
          )}

          {activeTab === "audit" && (
            <motion.div key="audit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="p-6 h-full flex flex-col gap-6">
              <div className="bg-[var(--hp-info-50)] text-[var(--hp-info-800)] p-4 rounded-xl border border-[var(--hp-info-200)] flex gap-3 text-sm">
                <Key className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Visibilité des Dossiers</p>
                  <p className="text-xs opacity-90 leading-relaxed">
                    Ce rapport présente tous les dossiers et la liste exacte des collaborateurs qui y ont accès.
                  </p>
                </div>
              </div>

              <div className="bg-surface border border-[var(--hp-color-border-muted)] rounded-xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <div className="overflow-x-hidden overflow-y-auto flex-1 p-0 scrollbar-thin scrollbar-thumb-gray-200 min-h-[300px]">
                  {auditLoading ? (
                    <MiniLoader />
                  ) : (
                    <table className="w-full text-left text-sm text-[var(--hp-color-text)]">
                      <thead className="bg-surface-muted sticky top-0 z-10 border-b border-[var(--hp-color-border-muted)]">
                        <tr>
                          <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Dossier</th>
                          <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Statut</th>
                          <th className="px-6 py-4 font-semibold text-subtle uppercase tracking-wider text-xs">Membres autorisés</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--hp-color-border-subtle)]">
                        {auditData.map((dossier) => (
                          <tr key={dossier.dossierId} className="hover:bg-surface-muted transition-colors align-top">
                            <td className="px-6 py-4">
                              <div className="font-bold text-primary flex items-center gap-2">
                                <FileText className="w-4 h-4 text-subtle" />
                                {dossier.reference}
                              </div>
                              <div className="text-sm text-tertiary mt-1">{dossier.title}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex px-2.5 py-1 rounded-md bg-workspace text-subtle border border-[var(--hp-color-border-muted)] text-xs font-semibold">
                                {dossier.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-2">
                                {dossier.membersWithAccess.map((member, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-xs border border-[var(--hp-color-border-subtle)] rounded-lg p-2 bg-surface">
                                    <div className="w-6 h-6 rounded-full bg-[var(--hp-info-100)] text-[var(--hp-info-700)] flex items-center justify-center font-bold">
                                      {member.name.charAt(0)}
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-semibold text-primary">{member.name}</p>
                                      <p className="text-[10px] text-tertiary">{member.jobTitle}</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {member.reasons.map((r, i) => (
                                        <span key={i} className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${r === 'Rôle Global' ? 'bg-[var(--hp-success-100)] text-[var(--hp-success-800)]' : r.includes('Exception') ? 'bg-[var(--hp-purple-100)] text-[var(--hp-purple-800)]' : 'bg-[var(--hp-gray-100)] text-[var(--hp-gray-800)]'}`}>
                                          {r}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                                {dossier.membersWithAccess.length === 0 && (
                                  <span className="text-xs text-subtle italic">Personne n'y a accès</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {auditData.length === 0 && (
                          <tr><td colSpan="3" className="px-6 py-12 text-center text-subtle">Aucun dossier trouvé.</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {openUserModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm" onClick={() => setOpenUserModal(false)} />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-surface rounded-xl shadow-hp-2xl w-full max-w-md overflow-hidden border border-[var(--hp-color-border-muted)]">
              <div className="flex justify-between items-center p-6 border-b border-[var(--hp-color-border-muted)]">
                <h3 className="text-xl font-bold text-primary">Inviter un collaborateur</h3>
                <button onClick={() => setOpenUserModal(false)} className="text-subtle hover:text-primary transition p-1"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={ajouterUtilisateur} className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-primary mb-1">Nom complet</label><input type="text" required className="workspace-input w-full" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Jean Dupont" /></div>
                <div><label className="block text-sm font-medium text-primary mb-1">Email</label><input type="email" required className="workspace-input w-full" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jean.dupont@hipparchus.fr" /></div>
                <div><label className="block text-sm font-medium text-primary mb-1">Mot de passe provisoire</label><input type="password" required className="workspace-input w-full" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Rôle assigné</label>
                  <CustomSelect
                    value={role}
                    onChange={setRole}
                    options={rolesWorkspace.length > 0 ? rolesWorkspace.map(r => ({ value: r.name, label: r.name })) : [{ value: "Conseiller", label: "Conseiller" }]}
                    placeholder="Sélectionner un rôle"
                  />
                </div>
                <div className="flex items-center gap-2 mt-4 bg-workspace p-3 rounded-lg border border-[var(--hp-color-border-muted)]">
                  <input type="checkbox" id="sendEmail" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 rounded border-[var(--hp-color-border-strong)] text-primary focus:ring-primary cursor-pointer" />
                  <label htmlFor="sendEmail" className="text-sm font-medium text-primary cursor-pointer select-none">Envoyer l'email d'invitation avec les accès</label>
                </div>
                <div className="pt-4 flex justify-end gap-3 border-t border-[var(--hp-color-border-subtle)] mt-4">
                  <button type="button" onClick={() => setOpenUserModal(false)} className="btn btn-secondary">Annuler</button>
                  <button type="submit" className="btn btn-primary bg-primary hover:bg-primary-hover text-white border-transparent">Créer le compte</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AlertModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={supprimerUtilisateur}
        title="Supprimer l'utilisateur ?"
        description="Cette action est irréversible. L'utilisateur n'aura plus accès à la plateforme."
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      <AnimatePresence>
        {editRoleModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm" onClick={() => setEditRoleModal(false)} />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-surface rounded-xl shadow-hp-2xl w-full max-w-md overflow-hidden border border-[var(--hp-color-border-muted)]">
              <div className="flex justify-between items-center p-6 border-b border-[var(--hp-color-border-muted)]">
                <h3 className="text-xl font-bold text-primary">Modifier le rôle</h3>
                <button onClick={() => setEditRoleModal(false)} className="text-subtle hover:text-primary transition p-1"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleUpdateRole} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Sélectionner un nouveau rôle</label>
                  <CustomSelect
                    value={roleToAssign}
                    onChange={setRoleToAssign}
                    options={[
                      { value: "", label: "@everyone (Accès basique)" },
                      ...rolesWorkspace.map(r => ({ value: r.name, label: r.name }))
                    ]}
                    placeholder="Sélectionner un rôle"
                  />
                </div>
                <div className="pt-4 flex justify-end gap-3 border-t border-[var(--hp-color-border-subtle)] mt-4">
                  <button type="button" onClick={() => setEditRoleModal(false)} className="btn btn-secondary">Annuler</button>
                  <button type="submit" className="btn btn-primary bg-[var(--hp-info-600)] hover:bg-[var(--hp-info-700)] text-white border-transparent">Enregistrer</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}